/**
 * Annual report indicators, one row per year, each row unfolding into what the
 * table has no column for.
 *
 * A row only becomes clickable when `yearDetail` finds something behind it, so
 * the chevron is a promise rather than a guess. The whole row carries the click;
 * the button in the year cell exists so the keyboard can reach it, and it owns
 * no handler of its own - its click bubbles to the row and toggles once.
 */
import { Fragment, useState } from "react";
import { BarChart3, ChevronRight } from "lucide-react";
import { cn } from "@wa/ui";
import {
  formatEurFull,
  formatEurShort,
  type ArReport,
  type ArReportActivity,
  type ArTaxData,
} from "../../../../lib/ariregister";
import { YearPanel } from "./YearPanel";
import { marginPct, yearDetail, type T, type YearDetail } from "./lib";

/** Table columns after the year, so the unfolded row can span the full width. */
const COL_COUNT = 8;

function pct(profit: number | null, revenue: number | null): string {
  const m = marginPct(profit, revenue);
  return m == null ? "-" : `${m.toFixed(1)}%`;
}

export function ReportTable({
  reports,
  tax,
  activities,
  t,
}: {
  /** Newest year first - the order the table draws. */
  reports: ArReport[];
  tax: ArTaxData[];
  activities: ArReportActivity[];
  t: T;
}) {
  const [openYear, setOpenYear] = useState<number | null>(null);

  const rows: { report: ArReport; detail: YearDetail | null }[] = reports.map((report) => ({
    report,
    detail: yearDetail(report, tax, activities),
  }));
  const anyExpandable = rows.some((r) => r.detail != null);

  return (
    <div className="mt-5">
      <div className="mb-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs font-medium text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <BarChart3 className="size-3.5" /> {t("ariregister.financial.reportIndicators")}
        </span>
        {anyExpandable && <span className="font-normal">{t("ariregister.financial.expandHint")}</span>}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[560px] text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs text-muted-foreground">
              <th className="py-1.5 pr-3 font-medium">{t("ariregister.detail.year")}</th>
              <th className="py-1.5 pr-3 text-right font-medium">{t("ariregister.financial.colRevenue")}</th>
              <th className="py-1.5 pr-3 text-right font-medium">{t("ariregister.financial.colOperating")}</th>
              <th className="py-1.5 pr-3 text-right font-medium">{t("ariregister.financial.colProfit")}</th>
              <th className="py-1.5 pr-3 text-right font-medium">{t("ariregister.financial.colEquity")}</th>
              <th className="py-1.5 pr-3 text-right font-medium">{t("ariregister.financial.colAssets")}</th>
              <th className="py-1.5 pr-3 text-right font-medium">{t("ariregister.financial.colMargin")}</th>
              <th className="py-1.5 text-right font-medium">{t("ariregister.detail.employees")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ report: r, detail }) => {
              const open = detail != null && openYear === r.year;
              return (
                <Fragment key={r.id}>
                <tr
                  className={cn(
                    "border-b border-border/50",
                    detail && "cursor-pointer transition-colors hover:bg-muted/40",
                    open && "bg-muted/30",
                  )}
                  onClick={detail ? () => setOpenYear(open ? null : r.year) : undefined}
                >
                  <td className="py-1.5 pr-3 font-medium">
                    {detail ? (
                      <button
                        type="button"
                        aria-expanded={open}
                        title={t("ariregister.financial.expandYear", { year: r.year })}
                        className="-ml-1 inline-flex items-center gap-1 rounded px-1 font-medium outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        <ChevronRight className={cn("size-3.5 text-muted-foreground transition-transform", open && "rotate-90")} />
                        {r.year}
                      </button>
                    ) : (
                      <span className="inline-flex items-center gap-1 pl-[1.125rem]">{r.year}</span>
                    )}
                    {r.isAudited && (
                      <span title={t("ariregister.financial.audited")} className="ml-1 text-[10px] text-primary">
                        ✓
                      </span>
                    )}
                  </td>
                  <td className="py-1.5 pr-3 text-right tabular-nums" title={formatEurFull(r.revenueCents)}>
                    {formatEurShort(r.revenueCents)}
                  </td>
                  <td className="py-1.5 pr-3 text-right tabular-nums" title={formatEurFull(r.operatingProfitCents)}>
                    {formatEurShort(r.operatingProfitCents)}
                  </td>
                  <td
                    className={cn(
                      "py-1.5 pr-3 text-right tabular-nums",
                      r.profitCents != null &&
                        (r.profitCents >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-destructive"),
                    )}
                    title={formatEurFull(r.profitCents)}
                  >
                    {formatEurShort(r.profitCents)}
                  </td>
                  <td className="py-1.5 pr-3 text-right tabular-nums" title={formatEurFull(r.equityCents)}>
                    {formatEurShort(r.equityCents)}
                  </td>
                  <td className="py-1.5 pr-3 text-right tabular-nums" title={formatEurFull(r.assetsCents)}>
                    {formatEurShort(r.assetsCents)}
                  </td>
                  <td className="py-1.5 pr-3 text-right tabular-nums text-muted-foreground">
                    {pct(r.profitCents, r.revenueCents)}
                  </td>
                  <td className="py-1.5 text-right tabular-nums">{r.employees ?? "-"}</td>
                </tr>
                {open && detail ? (
                  <tr className="border-b border-border/50 bg-muted/20">
                    {/* `w-0 min-w-full`: a definite width contributes nothing to the
                        table's column algorithm, so opening a row cannot reflow the
                        numbers above it - it then fills the cell it was given. */}
                    <td colSpan={COL_COUNT} className="p-0">
                      <div className="w-0 min-w-full p-2">
                        <YearPanel detail={detail} t={t} />
                      </div>
                    </td>
                  </tr>
                ) : null}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
